import { useState } from "react";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { ScreenLightbox } from "./ScreenLightbox";
import { useLang } from "@/i18n/LanguageContext";

const TEXTS = {
  de: {
    kicker: "Echte Screens aus dem Laden",
    title: "So sieht Mira im Geschäft aus",
    sub: "Original-Screenshots aus dem Kiosk-Modus: geführter Weg durch den Laden, Produkt-Scan, Sortiment und virtuelle Anprobe – und Mira empfiehlt im richtigen Moment passende Teile dazu. So steigert der Spiegel Ihre Zusatzverkäufe.",
    captions: {
      kiosk: "Kiosk-Start: Dein Weg durch den Laden",
      produkt: "Produkt scannen – Mira berät & empfiehlt Passendes dazu",
      scan: "Produkt scannen – Mira berät & empfiehlt Passendes dazu",
      sortiment: "Sortiment verwalten: Insta-Planer & Top-Artikel",
      tryon: "Anprobe im Geschäft: Teile wählen & anprobieren",
    },
  },
  en: {
    kicker: "Real in-store screens",
    title: "This is Mira inside the store",
    sub: "Original screenshots from kiosk mode: guided journey through the store, product scan, collection browser and virtual try-on – and Mira recommends matching pieces at the right moment, boosting your add-on sales.",
    captions: {
      kiosk: "Kiosk start: your journey through the store",
      produkt: "Scan a product – Mira advises & recommends matching pieces",
      scan: "Scan a product – Mira advises & recommends matching pieces",
      sortiment: "Manage your collection: Insta planner & top items",
      tryon: "In-store try-on: pick pieces & try them on",
    },
  },
  es: {
    kicker: "Pantallas reales en tienda",
    title: "Así es Mira dentro de la tienda",
    sub: "Capturas originales del modo kiosco: recorrido guiado por la tienda, escaneo de productos, catálogo y prueba virtual – y Mira recomienda piezas a juego en el momento justo, aumentando tus ventas adicionales.",
    captions: {
      kiosk: "Inicio kiosco: tu recorrido por la tienda",
      produkt: "Escanea un producto – Mira asesora y recomienda complementos",
      scan: "Escanea un producto – Mira asesora y recomienda complementos",
      sortiment: "Gestiona el catálogo: planificador de Insta y top artículos",
      tryon: "Prueba en tienda: elige piezas y pruébatelas",
    },
  },
  it: {
    kicker: "Schermate reali in negozio",
    title: "Ecco Mira dentro il negozio",
    sub: "Screenshot originali della modalità kiosk: percorso guidato nel negozio, scansione prodotti, catalogo e prova virtuale – e Mira consiglia capi abbinati al momento giusto, aumentando le tue vendite aggiuntive.",
    captions: {
      kiosk: "Avvio kiosk: il tuo percorso nel negozio",
      produkt: "Scansiona un prodotto – Mira consiglia e abbina",
      scan: "Scansiona un prodotto – Mira consiglia e abbina",
      sortiment: "Gestisci il catalogo: Insta planner e top articoli",
      tryon: "Prova in negozio: scegli i capi e provali",
    },
  },
  fr: {
    kicker: "Vrais écrans en boutique",
    title: "Voici Mira dans la boutique",
    sub: "Captures originales du mode kiosque : parcours guidé dans la boutique, scan produit, catalogue et essayage virtuel – et Mira recommande des pièces assorties au bon moment, pour augmenter tes ventes additionnelles.",
    captions: {
      kiosk: "Accueil kiosque : ton parcours en boutique",
      produkt: "Scanne un produit – Mira conseille et recommande des pièces assorties",
      scan: "Scanne un produit – Mira conseille et recommande des pièces assorties",
      sortiment: "Gère le catalogue : planificateur Insta et top articles",
      tryon: "Essayage en boutique : choisis des pièces et essaie-les",
    },
  },
};

const SCREENS = ["kiosk", "scan", "sortiment", "tryon"];

export const B2BScreens = () => {
  const { lang } = useLang();
  const t = TEXTS[lang] || TEXTS.de;
  const [idx, setIdx] = useState(null);

  return (
    <section className="mt-16" data-testid="b2b-screens-section">
      <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
        <Star size={11} /> {t.kicker}
      </p>
      <h2 className="mt-4 font-serif-display text-3xl font-semibold" data-testid="b2b-screens-title">{t.title}</h2>
      <p className="mt-3 max-w-2xl text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{t.sub}</p>

      <div className="mt-9 grid grid-cols-2 gap-5 sm:gap-7 lg:grid-cols-4">
        {SCREENS.map((key, i) => (
          <Reveal key={key} delay={i * 0.07}>
            <figure data-testid={`b2b-screen-${key}`}>
              <button
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`b2b-screen-zoom-${key}`}
                aria-label={t.captions[key]}
                className="block w-full cursor-zoom-in overflow-hidden rounded-[26px] border-[5px] shadow-lg transition-transform duration-500 hover:-translate-y-1.5"
                style={{ borderColor: "var(--on-surface)", background: "var(--on-surface)" }}
              >
                <img
                  src={`/app-screens/${lang}/${key}.webp?v=5`}
                  alt={t.captions[key]}
                  loading="lazy"
                  width={640}
                  height={1389}
                  className="w-full rounded-[21px]"
                />
              </button>
              <figcaption className="mt-3 text-center text-xs leading-relaxed" style={{ color: "var(--muted-warm)" }}>
                {t.captions[key]}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <ScreenLightbox screens={SCREENS} captions={t.captions} idx={idx} setIdx={setIdx} />
    </section>
  );
};
